import { useApp } from '../contexts/AppContext.js';
import { useOrganization } from '../contexts/OrganizationContext.js';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { useMemo } from 'react';

class ApiError extends Error {
    constructor(message, details) {
        super(message);
        this.name = 'ApiError';
        this.details = details;
    }
}
class ApiClient {
    constructor(getToken, organizationId, getApiUrl) {
        this.getToken = getToken;
        this.organizationId = organizationId;
        this.getApiUrl = getApiUrl;
    }
    async makeRequest(method, endpoint, data, config) {
        const baseURL = this.getApiUrl?.();
        if (!baseURL) {
            throw new Error('API base URL is not defined');
        }
        const url = `${baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
        // For multipart uploads (FormData), let axios/the browser set
        // `Content-Type: multipart/form-data` WITH the generated boundary —
        // forcing `application/json` here strips the boundary and the server
        // can't parse the body (the cause of failed support-ticket attachments).
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        const headers = {
            ...config?.headers,
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        };
        // Add auth token
        if (this.getToken) {
            const token = await this.getToken();
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }
        try {
            const response = await axios({
                method,
                url,
                data,
                headers,
                ...config,
            });
            return response.data;
        }
        catch (error) {
            if (error.response) {
                const data = error.response?.data;
                if (data?.error) {
                    throw new ApiError(data.error, data.details);
                }
                // Server responded with error status
                throw new ApiError(`Unknown error: ${error.response.status} ${error.response.statusText}`);
            }
            else if (error.request) {
                // Request was made but no response received
                throw new ApiError('Network error: No response from server');
            }
            else {
                // Something else happened
                throw new ApiError(`Request error: ${error.message}`);
            }
        }
    }
    async get(endpoint, config) {
        return this.makeRequest('get', endpoint, undefined, config);
    }
    async post(endpoint, data, config) {
        return this.makeRequest('post', endpoint, data, config);
    }
    async put(endpoint, data, config) {
        return this.makeRequest('put', endpoint, data, config);
    }
    async patch(endpoint, data, config) {
        return this.makeRequest('patch', endpoint, data, config);
    }
    async delete(endpoint, config) {
        return this.makeRequest('delete', endpoint, undefined, config);
    }
}
function useApiClient() {
    const { organization } = useOrganization();
    const { getToken } = useAuth();
    const { getApiUrl } = useApp();
    const apiClient = useMemo(() => {
        return new ApiClient(getToken, organization?.id, getApiUrl);
    }, [getToken, organization?.id, getApiUrl]);
    return apiClient;
}

export { ApiClient, ApiError, useApiClient };
//# sourceMappingURL=api-client.js.map
